import React from 'react';

interface ServerStatusProps {
  wsConnected: boolean;
  wsReadyState?: number;
  gamePin: string;
  phase: string;
  playerCount: number;
  error?: string | null;
  isCollapsed?: boolean;
  onToggle?: () => void;
}

const ServerStatus: React.FC<ServerStatusProps> = ({
  wsConnected,
  wsReadyState,
  gamePin,
  phase,
  playerCount,
  error,
  isCollapsed = true,
  onToggle
}) => {
  const getReadyStateLabel = (state?: number) => {
    switch (state) {
      case 0: return 'CONNECTING';
      case 1: return 'OPEN';
      case 2: return 'CLOSING';
      case 3: return 'CLOSED';
      default: return 'UNKNOWN';
    }
  };

  const getStatusColor = () => {
    if (error) return 'bg-red-50 border-red-200';
    if (wsConnected) return 'bg-green-50 border-green-200';
    return 'bg-yellow-50 border-yellow-200';
  };

  const getStatusIcon = () => {
    if (error) return '🔴';
    if (wsConnected) return '🟢';
    return '🟡';
  };

  // Collapsed view - minimal info with toggle button
  if (isCollapsed) {
    return (
      <button
        onClick={onToggle}
        className={`w-full rounded-lg border p-3 ${getStatusColor()} cursor-pointer hover:opacity-80 transition-opacity mt-4`}
        title="Click to expand server status"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">Server Status</span>
          <span className="text-xl">{getStatusIcon()}</span>
        </div>
      </button>
    );
  }

  // Expanded view - full details
  return (
    <div className={`rounded-lg border shadow-sm p-4 ${getStatusColor()} font-mono text-xs mt-4`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-gray-700 text-sm">Server Status</h4>
        <button
          onClick={onToggle}
          className="text-lg hover:opacity-70 transition-opacity"
          title="Click to collapse"
        >
          {getStatusIcon()}
        </button>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Connection:</span>
          <span className="font-semibold">{wsConnected ? 'Connected' : 'Disconnected'}</span>
        </div>

        {wsReadyState !== undefined && (
          <div className="flex justify-between">
            <span className="text-gray-600">WS State:</span>
            <span className="font-semibold">{getReadyStateLabel(wsReadyState)}</span>
          </div>
        )}

        {gamePin && (
          <div className="flex justify-between">
            <span className="text-gray-600">Game PIN:</span>
            <span className="font-semibold">{gamePin}</span>
          </div>
        )}

        <div className="flex justify-between">
          <span className="text-gray-600">Phase:</span>
          <span className="font-semibold">{phase}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-gray-600">Players:</span>
          <span className="font-semibold">{playerCount}</span>
        </div>

        {error && (
          <div className="mt-2 pt-2 border-t border-red-300">
            <span className="text-red-700 text-xs break-words">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerStatus;
